import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ShoppingCart,
  ClipboardList,
  ClipboardCheck,
  FileSearch,
  Sparkles,
  ChevronRight,
  Leaf,
} from "lucide-react";
import MessageBanner from "@/components/MessageBanner";
import { SEASONAL_RECOMMENDATIONS_ENABLED } from "@/config/features";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

export default function StoreHomePage() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const [storeName, setStoreName] = useState("");
  const [seasonalProducts, setSeasonalProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [recRes, stRes] = await Promise.all([
          axios.get(`${API}/seasonal-recommendations`, getAuthHeaders()).catch(() => ({ data: [] })),
          axios.get(`${API}/stores`, getAuthHeaders()).catch(() => ({ data: [] })),
        ]);

        const activeRecommendations = (recRes.data || [])
          .filter((rec) => rec.is_active)
          .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
          .slice(0, 6);
        setSeasonalProducts(activeRecommendations);

        const myStore = (stRes.data || []).find((s) => s.id === user.store_id);
        if (myStore) setStoreName(myStore.name);
      } catch (e) {
        // silent
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const primaryCard = {
    title: "発注する",
    description: "商品を選んで発注リストを作成",
    icon: ShoppingCart,
    path: "/store/order",
    testid: "home-card-order",
  };
  const secondaryCard = {
    title: "発注確認＆変更",
    description: "送信済みの発注内容を確認・変更",
    icon: ClipboardCheck,
    path: "/store/change",
    testid: "home-card-change",
  };
  const smallCards = [
    {
      title: "リストなし発注",
      description: "商品リストにない商品を発注",
      icon: ClipboardList,
      path: "/store/other",
      testid: "home-card-other",
    },
    {
      title: "変更申請状況",
      description: "申請済み変更の進捗を確認",
      icon: FileSearch,
      path: "/store/requests",
      testid: "home-card-requests",
    },
  ];

  const LightCard = ({ card }) => {
    const Icon = card.icon;
    return (
      <button
        data-testid={card.testid}
        onClick={() => navigate(card.path)}
        className="text-left group w-full rounded-2xl bg-white border border-slate-200 p-5 shadow-sm transition-all hover:border-emerald-300 hover:shadow-md"
      >
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-emerald-50 shrink-0">
            <Icon className="h-6 w-6 text-emerald-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-lg text-slate-800">{card.title}</div>
            <div className="text-sm text-slate-500 mt-0.5">{card.description}</div>
          </div>
          <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-emerald-500 group-hover:translate-x-1 transition-all shrink-0" />
        </div>
      </button>
    );
  };

  return (
    <div className="space-y-6 max-w-6xl">
      {/* Welcome hero */}
      <div className="relative overflow-hidden bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-100 rounded-2xl p-6 sm:p-8">
        <div className="relative z-10">
          <p className="text-xs text-emerald-700 mb-1 font-medium">やさまるポータルへようこそ</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">
            {storeName ? `${storeName} さん、こんにちは` : "こんにちは"}
          </h1>
          <p className="text-sm text-slate-600 mt-2">
            左メニューまたは下のカードから操作を始めてください。
          </p>
        </div>
        {/* 装飾イラスト（右側・丘と芽） */}
        <svg
          className="hidden md:block absolute right-0 bottom-0 h-full w-1/2 pointer-events-none"
          viewBox="0 0 400 200"
          preserveAspectRatio="xMaxYMax slice"
          aria-hidden="true"
        >
          <path d="M0 160 Q120 110 260 150 T400 130 L400 200 L0 200 Z" fill="#bbf7d0" opacity="0.55" />
          <path d="M120 175 Q240 130 400 165 L400 200 L120 200 Z" fill="#86efac" opacity="0.55" />
          <g transform="translate(300 70)">
            <path d="M8 90 L8 40" stroke="#4ade80" strokeWidth="4" strokeLinecap="round" />
            <path d="M8 55 C-14 50 -22 28 -20 14 C-4 16 10 34 8 55 Z" fill="#4ade80" opacity="0.85" />
            <path d="M8 44 C26 38 34 18 32 6 C16 8 4 24 8 44 Z" fill="#22c55e" opacity="0.85" />
          </g>
        </svg>
      </div>

      {/* お知らせ（Welcomeバナーの下に表示） */}
      <MessageBanner />

      {/* クイックアクション */}
      <div>
        <h2 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-emerald-600" />
          クイックアクション
        </h2>

        {/* Row 1: 発注する（大・緑）＋ 発注確認＆変更 */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          <button
            data-testid={primaryCard.testid}
            onClick={() => navigate(primaryCard.path)}
            className="lg:col-span-3 text-left group rounded-2xl p-6 bg-gradient-to-br from-emerald-500 to-green-600 shadow-sm transition-all hover:from-emerald-600 hover:to-green-700 hover:shadow-lg"
          >
            <div className="flex items-center gap-4">
              <div className="p-3.5 rounded-2xl bg-white/20 shrink-0">
                <ShoppingCart className="h-7 w-7 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-2xl font-bold text-white">{primaryCard.title}</div>
                <div className="text-sm text-emerald-50 mt-1">{primaryCard.description}</div>
              </div>
              <div className="p-2 rounded-full bg-white/20 shrink-0 group-hover:translate-x-1 transition-transform">
                <ChevronRight className="h-5 w-5 text-white" />
              </div>
            </div>
          </button>

          <div className="lg:col-span-2">
            <LightCard card={secondaryCard} />
          </div>
        </div>

        {/* Row 2: 小カード */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          {smallCards.map((card) => (
            <LightCard key={card.path} card={card} />
          ))}
        </div>
      </div>

      {/* 今月のおすすめ (季節商品) — 運用可能になるまで非表示（features.js のフラグで再開） */}
      {SEASONAL_RECOMMENDATIONS_ENABLED && (
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Leaf className="h-4 w-4 text-emerald-600" />
            今月のおすすめ（旬の食材）
          </h2>
          <button
            onClick={() => navigate("/store/seasonal-recommendations")}
            className="text-xs text-emerald-700 hover:text-emerald-900 font-medium"
          >
            もっと見る →
          </button>
        </div>
        {loading ? (
          <div className="text-center py-8 text-slate-500">読み込み中...</div>
        ) : seasonalProducts.length === 0 ? (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 text-center text-sm text-slate-500">
            現在おすすめの旬の食材はありません
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {seasonalProducts.map((rec) => (
              <button
                key={rec.id}
                data-testid={`home-seasonal-${rec.id}`}
                onClick={() => navigate("/store/seasonal-recommendations")}
                className="bg-white border-2 border-slate-200 rounded-2xl overflow-hidden hover:border-emerald-400 hover:shadow-md transition-all group text-left"
              >
                {rec.image_url ? (
                  <div className="relative overflow-hidden">
                    <img
                      src={`${BACKEND_URL}${rec.image_url}`}
                      alt={rec.title}
                      className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-2 right-2 bg-emerald-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                      おすすめ
                    </div>
                  </div>
                ) : (
                  <div className="relative h-40 bg-gradient-to-br from-emerald-50 to-emerald-100 flex items-center justify-center">
                    <Leaf className="h-12 w-12 text-emerald-300" />
                    <div className="absolute top-2 right-2 bg-emerald-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                      おすすめ
                    </div>
                  </div>
                )}
                <div className="p-4">
                  <h3 className="font-bold text-base text-slate-800 mb-1 line-clamp-1">
                    {rec.title}
                  </h3>
                  <p className="text-sm text-slate-600 line-clamp-2 mb-2">
                    {rec.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">
                      {rec.start_date} 〜 {rec.end_date}
                    </span>
                    <ChevronRight className="h-4 w-4 text-emerald-600 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      )}
    </div>
  );
}
